import {
    Component,
    HostListener,
    OnInit,
    OnDestroy,
    ViewChild,
    ElementRef,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { distinctUntilChanged, skip, takeUntil } from 'rxjs/operators';
import { SafeResourceUrl } from '@angular/platform-browser';
import { SsoService } from '@app/core/sso.service';
import { EnterpriseNavigationService } from '@app/core/enterprise-navigation.service';
import { CommonService } from '@app/core/common.service';
import { OrganizationList } from '@app/models/organization.model';

const READINESS_TIMEOUT_MS = 15000;

interface EnterprisePortalMessage {
    type: string;
    nonce?: string;
    code?: string;
    enterpriseOrgId?: string;
    enterpriseNetworkId?: string;
    referralId?: string;
    direction?: string;
    unreadCount?: number;
}

@Component({
    selector: 'app-notification-list-drawer',
    templateUrl: './notification-list-drawer.component.html',
    styleUrls: ['./notification-list-drawer.component.scss'],
    standalone: false,
})
export class NotificationListDrawerComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsIframe') notificationsIframe!: ElementRef<HTMLIFrameElement>;

    isDrawerOpen = false;
    iframeUrl: SafeResourceUrl | null = null;
    isLoading = true;
    hasError = false;
    hasAccessError = false;

    private readinessTimeout: number | null = null;
    private hasRetried = false;
    private enterpriseOrigin = '';
    private readonly destroy$ = new Subject<void>();

    constructor(
        private ssoService: SsoService,
        private router: Router,
        private enterpriseNavigationService: EnterpriseNavigationService,
        private commonService: CommonService,
    ) {
        this.enterpriseOrigin = this.ssoService.getEnterpriseOrigin();
    }

    ngOnInit(): void {
        this.loadPortal();

        this.commonService.organizationListInfo.pipe(
            distinctUntilChanged((a: OrganizationList, b: OrganizationList) => a.id === b.id),
            skip(1),
            takeUntil(this.destroy$),
        ).subscribe(() => {
            this.hasRetried = false;
            this.loadPortal();
        });
    }

    open(): void {
        this.isDrawerOpen = true;
        if (this.hasError) {
            this.hasRetried = false;
            this.loadPortal();
        }
    }

    close(): void {
        this.isDrawerOpen = false;
        this.clearReadinessTimeout();
    }

    loadPortal(): void {
        this.isLoading = true;
        this.hasError = false;
        this.hasAccessError = false;
        this.iframeUrl = this.ssoService.buildIframeUrl('/notifications');
        this.startReadinessTimeout();
    }

    @HostListener('window:message', ['$event'])
    onMessage(event: MessageEvent): void {
        if (!this.enterpriseOrigin || event.origin !== this.enterpriseOrigin) return;

        // Fix 1: Only handle messages from this component's own iframe to prevent
        // cross-iframe SSO contamination when both the notification drawer and the
        // enterprise container iframes are active simultaneously on the same origin.
        // The guard is unconditional (no iframeWindow && prefix) so that a null/undefined
        // contentWindow during early lifecycle still rejects the message.
        const iframeWindow = this.notificationsIframe?.nativeElement?.contentWindow;
        if (event.source !== iframeWindow) return;

        const data = event.data as EnterprisePortalMessage;
        if (!data?.type) return;

        switch (data.type) {
            case 'SSO_READY':
                this.clearReadinessTimeout();
                this.isLoading = false;
                break;

            case 'SSO_ERROR':
                if (!this.hasRetried) {
                    this.hasRetried = true;
                    this.loadPortal();
                } else {
                    this.handleError();
                }
                break;

            case 'SSO_NONCE_REQUEST':
                this.handleNonceRequest();
                break;

            case 'NAVIGATE_TO_REFERRAL':
                if (!this.isDrawerOpen) return;
                if (data.referralId && data.direction) {
                    this.handleNavigateToReferral(data.referralId, data.direction);
                }
                break;
        }
    }

    private handleNavigateToReferral(referralId: string, direction: string): void {
        this.close();
        const isOnReferrals = this.router.url.split('?')[0] === '/home/referrals';
        if (!isOnReferrals) {
            // Fix 4: Reset any stale containerWindow so navigateToReferral stores a
            // pendingNavigation for the fresh enterprise container iframe rather than
            // posting to a dead window from a previous session.
            this.enterpriseNavigationService.setEnterpriseContainerWindow(null, '');
            // Navigate with a normal push so the user can still press Back to return
            // to whatever page they were on before clicking the notification.
            this.router.navigate(['/home/referrals']);
        }
        // If already on /home/referrals the enterprise container window is still live;
        // navigateToReferral posts the NAVIGATE_REFERRAL message directly without
        // causing a full iframe reload or redundant SSO handshake.
        this.enterpriseNavigationService.navigateToReferral(referralId, direction);
    }

    private handleNonceRequest(): void {
        this.ssoService.getNonce(true).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response) => {
                if (response?.nonce) {
                    this.postMessageToIframe({
                        type: 'SSO_NONCE_RESPONSE',
                        nonce: response.nonce,
                        ...(response.enterprise_org_id && { enterpriseOrgId: response.enterprise_org_id }),
                        ...(response.enterprise_network_id && { enterpriseNetworkId: response.enterprise_network_id }),
                    });
                } else {
                    this.postMessageToIframe({ type: 'SSO_NONCE_ERROR' });
                }
            },
            error: (err) => {
                if (err instanceof HttpErrorResponse && err.status === 422 && err.error?.error === 'enterprise_org_not_found') {
                    this.handleError();
                    return;
                }
                // A 404 means the user has no IAM mapping (not provisioned). Show the
                // friendly "contact your administrator" screen rather than the generic
                // error + retry.
                if (err instanceof HttpErrorResponse && err.status === 404) {
                    this.hasAccessError = true;
                    this.handleError();
                    return;
                }
                this.postMessageToIframe({ type: 'SSO_NONCE_ERROR' });
                if (err instanceof HttpErrorResponse && err.status === 403) {
                    this.hasRetried = true;
                }
            },
        });
    }

    private postMessageToIframe(message: EnterprisePortalMessage): void {
        const iframe = this.notificationsIframe?.nativeElement;
        if (iframe?.contentWindow) {
            iframe.contentWindow.postMessage(message, this.enterpriseOrigin);
        }
    }

    private startReadinessTimeout(): void {
        this.clearReadinessTimeout();
        this.readinessTimeout = window.setTimeout(() => {
            if (this.isLoading) this.handleError();
        }, READINESS_TIMEOUT_MS);
    }

    private clearReadinessTimeout(): void {
        if (this.readinessTimeout) {
            clearTimeout(this.readinessTimeout);
            this.readinessTimeout = null;
        }
    }

    private handleError(): void {
        this.isLoading = false;
        this.hasError = true;
        this.clearReadinessTimeout();
    }

    retry(): void {
        this.hasRetried = false;
        this.loadPortal();
    }

    ngOnDestroy(): void {
        this.clearReadinessTimeout();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
